import React, { useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useScrollLock } from '../../../../../_utils/useScrollLock'
import { hapticTick } from '../../../../../_utils/haptics'
import {
  salonReviews, subscribeSalonReviews, ensureSalonReviews, loadAllSalonReviews,
  matchReviews, matchSpans, searchWords, reviewWhen, REVIEWS_CAP,
} from '../data/reviews'
import { usedSources } from '../data/store'
import { CloseIcon, SearchIcon } from './icons'

export const Stars = ({ n }) => <span className="sx-stars-read" aria-label={`${n} out of 5 stars`}>{'★★★★★'.slice(0, n)}<span>{'★★★★★'.slice(n)}</span></span>

// The salon's reviews: one copy for everything that shows them. keyOf, when
// given, says which changes matter here, so a page full of previews doesn't
// draw again for every older page the panel loads.
export const useSalonReviews = (keyOf = null) => {
  const [state, setState] = useState(salonReviews)
  useEffect(() => {
    let last = keyOf ? keyOf(salonReviews()) : null
    const off = subscribeSalonReviews((s) => {
      if (keyOf) {
        const key = keyOf(s)
        if (key === last) return
        last = key
      }
      setState(s)
    })
    setState(salonReviews())
    ensureSalonReviews()
    return off
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  return state
}
// Only the first page (the newest reviews): a fresh load or a new salon
export const firstPageKey = (s) => `${s.salonId}|${s.at}|${s.failed}`

// The words typed, marked where they are
const Marked = ({ text, words }) => {
  const spans = matchSpans(text, words)
  if (!spans.length) return text
  const out = []
  let at = 0
  spans.forEach(([start, end]) => {
    if (start > at) out.push(text.slice(at, start))
    out.push(<mark key={start}>{text.slice(start, end)}</mark>)
    at = end
  })
  if (at < text.length) out.push(text.slice(at))
  return out
}

const plural = (n, one, many) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`
const STAR_CHIPS = [0, 5, 4, 3, 2, 1]
const STEP = 30

// Every review in one panel: search by name, words or service, filter by
// stars, newest first. Opening it loads the rest in the background, so the
// search looks through all of them. 'pick' (the studio): tap one to use it
// in the post. 'make' (the Social page): tap one to make a post from it.
const ReviewsPanel = ({ mode = 'pick', currentId = null, onUse, onClose }) => {
  useScrollLock(true)
  const reviews = useSalonReviews()
  const [query, setQuery] = useState('')
  const [stars, setStars] = useState(0)
  const [count, setCount] = useState(STEP)
  const bodyRef = useRef(null)
  const endRef = useRef(null)
  const searchRef = useRef(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const used = useMemo(() => usedSources(), [])

  useEffect(() => { loadAllSalonReviews() }, [])
  useEffect(() => {
    const back = document.activeElement
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      if (searchRef.current && searchRef.current.value) setQuery('')
      else closeRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    // Straight into the search on a computer; on a phone the keyboard would
    // cover the list
    const fine = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches
    if (fine && searchRef.current) searchRef.current.focus({ preventScroll: true })
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (back && back.focus && document.contains(back)) back.focus({ preventScroll: true })
    }
  }, [])

  const words = useMemo(() => searchWords(query), [query])
  const shown = useMemo(() => matchReviews(reviews.list, { stars, query }), [reviews.list, stars, query])
  // A new search or filter starts from the top
  useEffect(() => {
    setCount(STEP)
    if (bodyRef.current) bodyRef.current.scrollTop = 0
  }, [stars, query])
  // The next few dozen as the end comes near
  const moreToShow = count < shown.length
  useEffect(() => {
    const el = endRef.current
    if (!moreToShow || !el || typeof IntersectionObserver === 'undefined') return undefined
    const io = new IntersectionObserver((es) => { if (es.some(e => e.isIntersecting)) setCount(n => n + STEP) }, { root: bodyRef.current, rootMargin: '0px 0px 600px 0px' })
    io.observe(el)
    return () => io.disconnect()
  }, [moreToShow, count])

  const waiting = !reviews.at && !reviews.failed
  const older = reviews.hasMore && reviews.list.length < REVIEWS_CAP
  const none = !!reviews.at && !reviews.list.length && !older
  const looking = older && reviews.older !== 'failed'
  const filtering = !!stars || words.length > 0
  const q = query.trim()
  const starText = stars ? ` with ${plural(stars, 'star', 'stars')}` : ''
  const clear = () => { hapticTick(); if (query) { setQuery(''); if (searchRef.current) searchRef.current.focus({ preventScroll: true }) } else setStars(0) }
  const stat = reviews.average ? `${reviews.average.toFixed(1)} stars${reviews.total ? ` from ${plural(reviews.total, 'review', 'reviews')}` : ''}. ` : ''

  const panel = (
    <div className="sx-sheet-backdrop" onPointerDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="sx-sheet sx-rv-sheet" role="dialog" aria-modal="true" aria-labelledby="sx-rv-title">
        <div className="sx-rv-head">
          <div className="sx-sheet-head">
            <div className="sx-rv-titles">
              <h2 className="sx-sheet-title" id="sx-rv-title">Your reviews</h2>
              <p className="sx-rv-sub">{stat}{mode === 'pick' ? 'Tap one to use it in this post.' : 'Tap one to make a post from it.'}</p>
            </div>
            <button type="button" className="sx-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
          </div>
          {!none && !reviews.failed && (
            <>
              <div className="sx-rv-search">
                <SearchIcon size={16} />
                <input
                  ref={searchRef}
                  className="sx-input"
                  type="search"
                  value={query}
                  placeholder="Search by name, words or service"
                  aria-label="Search your reviews"
                  enterkeyhint="search"
                  onChange={(e) => { const v = e.target.value; setQuery(v) }}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
                />
                {query && <button type="button" className="sx-rv-clear" onClick={clear} aria-label="Clear the search"><CloseIcon size={14} /></button>}
              </div>
              <div className="sx-rv-stars" role="radiogroup" aria-label="Stars">
                {STAR_CHIPS.map(n => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={stars === n}
                    aria-label={n ? plural(n, 'star', 'stars') : 'All'}
                    className={`sx-rv-chip${stars === n ? ' is-on' : ''}`}
                    onClick={() => { if (stars !== n) { hapticTick(); setStars(n) } }}
                  >
                    {n ? <>{n}<span aria-hidden>★</span></> : 'All'}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="sx-rv-body" ref={bodyRef}>
          {filtering && !waiting && shown.length > 0 && (
            <p className="sx-rv-count" aria-live="polite">
              {plural(shown.length, 'review', 'reviews')}{starText}{q ? `${shown.length === 1 ? ' matches' : ' match'} “${q}”` : ''}{looking ? ' so far' : ''}
            </p>
          )}
          {waiting && [0, 1, 2, 3].map(i => <span key={i} className="sx-rp-row is-loading" aria-hidden><span className="sx-line" /><span className="sx-line is-short" /></span>)}
          {reviews.failed && (
            <div className="sx-rv-note">
              <p>Your reviews couldn’t be loaded just now.</p>
              <button type="button" className="sx-btn" onClick={() => { hapticTick(); loadAllSalonReviews() }}>Try again</button>
            </div>
          )}
          {none && <div className="sx-rv-note"><p>No reviews yet. When clients leave one, it shows here.</p></div>}

          {shown.slice(0, count).map((r) => {
            const on = String(r.id) === currentId
            return (
              <button key={r.id} type="button" className={`sx-rp-row sx-rv-row${on ? ' is-on' : ''}`} aria-pressed={mode === 'pick' ? on : undefined} onClick={() => onUse(r)}>
                <span className="sx-rp-row-top">
                  <Stars n={r.rating} />
                  <span className="sx-review-when">{reviewWhen(r.at)}{r.google ? ' · Google' : ''}</span>
                </span>
                <span className="sx-rp-text">“<Marked text={r.text} words={words} />”</span>
                <span className="sx-rp-row-foot">
                  <span className="sx-rv-who"><Marked text={r.author} words={words} />{r.service ? <>, <Marked text={r.service} words={words} /></> : null}</span>
                  {on ? <span className="sx-rp-tag is-on">In this post</span> : used[`review:${r.id}`] ? <span className="sx-rp-tag">Shared before</span> : null}
                </span>
              </button>
            )
          })}
          {moreToShow && <button type="button" ref={endRef} className="sx-btn sx-rv-more" onClick={() => setCount(n => n + STEP)}>Show more</button>}

          {!waiting && !reviews.failed && !none && !moreToShow && (
            <div className="sx-rv-foot">
              {filtering && !shown.length && !looking && (
                <div className="sx-rv-note">
                  <p>{q ? `No reviews${starText} match “${q}”.` : `No reviews${starText}.`}</p>
                  <button type="button" className="sx-btn" onClick={clear}>{q ? 'Clear the search' : 'Show every review'}</button>
                </div>
              )}
              {looking && <p className="sx-rv-status">{filtering ? 'Looking through older reviews' : 'Loading older reviews'}<span className="sx-dots" aria-hidden><i /><i /><i /></span></p>}
              {reviews.older === 'failed' && (
                <p className="sx-rv-status">
                  Some older reviews couldn’t be loaded.
                  <button type="button" className="sx-link" onClick={() => { hapticTick(); loadAllSalonReviews() }}>Try again</button>
                </p>
              )}
              {!older && reviews.hasMore && <p className="sx-rv-status">Showing your newest {REVIEWS_CAP.toLocaleString('en-GB')} reviews.</p>}
              {!reviews.hasMore && !filtering && reviews.next > reviews.list.length && <p className="sx-rv-status">Reviews with stars and no words aren’t shown, as there’s nothing to share.</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
  return ReactDOM.createPortal(panel, document.body)
}

export default ReviewsPanel
